import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';

import { getBaseUrl, isProduction } from 'helpers/environment';
import { showSnackbar } from 'helpers/snackbar';

import {
  APP_BANNER_HEIGHT,
  HEADER_Z_INDEX,
  Layout,
  NavItem,
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_PADDING_X,
  SIDEBAR_PRIMARY_WIDTH
} from 'ds';
import { APP_ROOT_PATH, QA_LINK, REFERRALS_PATH } from 'routes';
import { BLUE_500, GRAY_50_HEX, WHITE, colorClassName } from 'shared';
import { selectUIState } from 'store/UI/selectors';
import { selectIsCustomerUser, selectUser } from 'store/User/selectors';
import { useAppSelector } from 'store/hooks';
import { AI_ASSISTANT_PATH, TENANT_ONBOARDING_CHECKLIST_PATH } from 'routes';

import NavIcon from './NavIcon';
import Navatar from './Navatar';
import SidebarItems from './SidebarItems';
import { REFERRALS_NAV_ITEM } from './constants';
import { isNavigationItemActive } from '../utils';

import { ReactComponent as SidebarCLogo } from './sidebar-c-logo.svg';
import { ReactComponent as SidebarLogo } from './sidebar-logo.svg';

interface Props {
  items: NavItem[];
  theme: 'light' | 'dark' | 'white' | 'dark_blue';
  avatarUrl?: string;
  isExpandable?: boolean;
}

const THEME = {
  white: { color: 'white' },
  light: { color: 'gray-25' },
  dark: { color: 'blue-25' },
  dark_blue: { color: 'blue-900' }
} as const;

const Sidebar: React.FC<Props> = ({ theme, items, avatarUrl }) => {
  const { pathname, search } = useLocation();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const uxType = useAppSelector(selectUIState).uxType;
  const isCustomerUx = uxType === 'customer';
  const user = useAppSelector(selectUser);
  const isCustomerUser = useAppSelector(selectIsCustomerUser);
  const isAppPath = pathname.startsWith(APP_ROOT_PATH);
  const { appBannerType, showChecklistSkipPopover } = useAppSelector(selectUIState);
  const isLandlordUx = uxType === 'landlord';
  const height = isAppPath && appBannerType ? `calc(100vh - ${APP_BANNER_HEIGHT}px)` : '100vh';
  const appBannerIsVisible = appBannerType && isAppPath;
  const isOnDashboard = pathname === AI_ASSISTANT_PATH || pathname.startsWith(AI_ASSISTANT_PATH + '/');
  const isOnChecklistPage = pathname === TENANT_ONBOARDING_CHECKLIST_PATH;
  const shouldBlockInteractions = showChecklistSkipPopover && isOnDashboard && !isOnChecklistPage;

  return (
    <>
      <Layout
        width={SIDEBAR_PRIMARY_WIDTH}
        height={height}
        position="sticky"
        top={appBannerIsVisible ? APP_BANNER_HEIGHT : 0}
        flexShrink={0}
      />

      <Layout
        width={isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_PRIMARY_WIDTH}
        color={THEME[theme].color}
        paddingTop={24}
        height={height}
        position="fixed"
        top={appBannerIsVisible ? APP_BANNER_HEIGHT : 0}
        zIndex={HEADER_Z_INDEX + 2}
        flexShrink={0}
        direction="column"
        justify="space-between"
        paddingBottom={32}
        onMouseEnter={() => {
          if (!shouldBlockInteractions) {
            setIsExpanded(true);
          }
        }}
        onMouseLeave={() => {
          if (!shouldBlockInteractions && !menuOpen) {
            setIsExpanded(false);
          }
        }}
        transition="width 160ms ease-in-out"
        __style={{
          borderRight: `1px solid ${GRAY_50_HEX}`,
          pointerEvents: shouldBlockInteractions ? 'none' : 'auto'
        }}
      >
        <Layout paddingX={SIDEBAR_PADDING_X}>
          <Layout paddingLeft={2} align="center" justify="space-between">
            <Layout position="relative">
              <span className={classNames('Icon', isLandlordUx ? colorClassName(WHITE) : colorClassName(BLUE_500))}>
                <SidebarCLogo style={{ display: 'inline-flex' }} />
              </span>
              <Layout top={0} left={2} position="absolute" opacity={isExpanded ? 1 : 0} transition="opacity 60ms">
                <span className={classNames('Icon', isLandlordUx ? colorClassName(WHITE) : colorClassName(BLUE_500))}>
                  <SidebarLogo style={{ display: 'inline-flex' }} />
                </span>
              </Layout>
            </Layout>
          </Layout>
          <Layout marginTop={24} style={{ pointerEvents: shouldBlockInteractions ? 'none' : 'auto' }}>
            <SidebarItems items={items} isExpanded={isExpanded} theme={theme} />
          </Layout>
        </Layout>
        <Layout paddingX={12} style={{ pointerEvents: shouldBlockInteractions ? 'none' : 'auto' }}>
          {!isProduction() && (
            <Layout marginBottom={8}>
              <NavIcon
                iconName="externalLink"
                label="QA Link"
                theme={theme}
                isActive={false}
                isExpanded={isExpanded}
                onClick={() => {
                  const params = new URLSearchParams();
                  params.append('path', pathname);

                  if (search) {
                    params.append('search', search);
                  }

                  if (user) {
                    params.append('email', user.email);
                  }

                  const url = new URL(QA_LINK + '?' + params.toString(), getBaseUrl()).toString();
                  copy(url);
                  showSnackbar({ message: 'Copied' });
                }}
              />
            </Layout>
          )}
          {isAppPath && (
            <>
              {isCustomerUx && isCustomerUser && (
                <Layout marginBottom={8}>
                  <NavIcon
                    iconName={REFERRALS_NAV_ITEM.iconName}
                    href={REFERRALS_NAV_ITEM.href}
                    label={REFERRALS_NAV_ITEM.label}
                    theme={theme}
                    isActive={isNavigationItemActive({ href: REFERRALS_PATH, exact: true, pathname })}
                    isExpanded={isExpanded}
                  />
                </Layout>
              )}
              <Layout borderTop marginTop={8} marginBottom={12} borderColor="blue-200" />
            </>
          )}
          {avatarUrl && (
            <Navatar
              avatarUrl={avatarUrl}
              isExpanded={isExpanded}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
              setIsExpanded={setIsExpanded}
            />
          )}
        </Layout>
      </Layout>
    </>
  );
};

export default Sidebar;
